import { inc } from 'alphacounter';
import { Browserbase } from 'browserbase';
import { createId } from 'crypto-id';
import { JSONPatch } from '../json-patch';
import { blockableByPath } from '../lib/concurrency';
import { getDocConfig } from '../lib/config';
import { agreeableDatabase } from '../lib/db';
import logging from '../log';
import { EMPTY_REV, REV_LENGTH, breakPatchIntoSmallerChanges, textConversion } from '../util';
import { clearMemoized, memoize } from './memoize';
import { agreeableSyncer } from './syncer';
const localChangeApplyOptions = { strict: true, createMissingObjects: true };
const remoteChangeApplyOptions = { partial: true, createMissingObjects: true };
const log = logging.tagColor('Leader', 'Purple');
export async function agreeableWorkerLeader(config, options, tab) {
    let { uid } = options;
    const db = agreeableDatabase(uid);
    globalThis.db = db;
    const syncer = await agreeableSyncer(config, options, tab, db);
    let moduleUnsubscriber;
    const leader = {
        open,
        close,
        getDB,
        get: blockableByPath(memoize(get, 1)),
        patch: addCallerId(blockableByPath(clearMemoized(patch, get, 1))),
        delete: deleteDoc,
        deleteDatabase,
        hasUncommittedChanges,
        auth: syncer.auth,
        connect: syncer.connect,
        disconnect: syncer.disconnect,
        sync: syncer.sync,
        unsync: syncer.unsync,
        hadActivity: syncer.hadActivity,
    };
    Object.setPrototypeOf(leader, null);
    return leader;
    async function open() {
        try {
            await db.open();
            syncer.start(); // asynchronous, may not happen at all if offline
            tab.setState({ ...tab.getState(), active: true });
            if (typeof options.leaderModule === 'function') {
                moduleUnsubscriber = (await options.leaderModule(config, options, extendLeaderAPI, leader)) || undefined;
            }
        }
        catch (e) {
            console.error(e);
            close();
            tab.setState({ error: e });
        }
    }
    function close() {
        tab.setState({ active: false, connected: false, online: false, authed: false });
        syncer.stop();
        db.close();
        moduleUnsubscriber?.();
        Object.setPrototypeOf(leader, null);
    }
    function getDB() {
        return db;
    }
    function extendLeaderAPI(api) {
        // Insert this new API into the prototype chain of leader to be accessable by clients
        Object.setPrototypeOf(leader, Object.setPrototypeOf(api, Object.getPrototypeOf(leader)));
    }
    /**
     * Return a document to the client, loading it from the database. Keep it in memory for a second in case others ask
     * for it at the same moment (e.g. when a browser opens multiple tabs on startup).
     */
    async function get(path) {
        const docConfig = getDocConfig(config, path);
        const { type, hasText } = docConfig;
        const dbDoc = db.doc(path);
        const trackLocalDoc = type === 'operational_transformation';
        let [{ doc, rev } = { doc: null, rev: '' }, changes] = await Promise.all([dbDoc.getDoc(), dbDoc.getChanges()]);
        let i = 0;
        let patches = [];
        for (; i < changes.length; i++) {
            if (trackLocalDoc && !changes[i].committed)
                break;
            patches.push(...changes[i].patch);
        }
        if (i > 0) {
            doc = new JSONPatch(patches, hasText).apply(doc, remoteChangeApplyOptions);
            rev = changes[i - 1].rev;
            patches.length = 0;
            changes = changes.slice(i);
        }
        for (i = 0; i < changes.length; i++) {
            patches.push(...changes[i].patch);
        }
        if (i > 0) {
            doc = new JSONPatch(patches, hasText).apply(doc, localChangeApplyOptions);
            rev = changes[i - 1].rev;
        }
        if (hasText) {
            doc = textConversion.replace[hasText](doc);
        }
        return { doc, rev, changes };
    }
    /**
     * Patch a document in the database and send the patch to the clients.
     */
    async function patch(callerId, path, patch) {
        log('patch', patch[0].value);
        const docConfig = getDocConfig(config, path);
        const type = docConfig.type;
        const dbDoc = db.doc(path);
        let rev = (await dbDoc.getLastRev()) || EMPTY_REV;
        // Break up into < 1MB chunks for large changes (e.g. pasting a large amount of text into a document)
        const patchChunks = breakPatchIntoSmallerChanges(patch);
        const changes = patchChunks.map(patch => {
            rev = inc(rev, REV_LENGTH);
            return type === 'operational_transformation'
                ? { rev, id: createId(8), patch, created: syncer.now() }
                : { rev, patch };
        });
        await dbDoc.putChanges(changes);
        syncer.changesCreated(path, changes);
        // Send to all clients except the one that made the change
        const exceptToCaller = `-${callerId}`;
        log('send to tabs', changes[0].id ? changes.map(c => `${c.rev} | ${c.id}`) : changes.map(c => c.rev));
        tab.send({ path, changes }, exceptToCaller);
        return changes;
    }
    /**
     * Delete the database, optionally re-creating a new one.
     */
    async function deleteDoc(path) {
        const dbDoc = db.doc(path);
        // Though we only need to delete the top-level doc on the server, all sub-docs will be deleted as well and paths returned
        const paths = await dbDoc.markDocForDelete(path);
        syncer.docDeleted(path);
        paths.forEach(path => {
            tab.send({ path, deleted: true });
        });
    }
    async function deleteDatabase(dbUIDOrRecreate) {
        if (typeof dbUIDOrRecreate === 'string' && dbUIDOrRecreate !== uid) {
            await Browserbase.deleteDatabase(`agreeable/${dbUIDOrRecreate}`);
        }
        else {
            await db.deleteDatabase();
            if (dbUIDOrRecreate === true) {
                await db.open();
            }
        }
    }
    async function hasUncommittedChanges() {
        return (await db.stores.changes.where('committed').equals(0).count()) > 0;
    }
    function addCallerId(fn) {
        return function (...args) {
            args.unshift(tab.getCurrentCallerId());
            return fn.apply(this, args);
        };
    }
}
